import { alpha, styled } from '@mui/material';

export const EditorStylesBase = styled('div')(({ theme }) => ({
  '--theme-spacing': theme.spacing(1),

  '--theme-color-purple': theme.palette.communicate[500],
  '--theme-color-red': theme.palette.error.main,
  '--theme-color-yellow': theme.palette.warning.main,
  '--theme-color-blue': theme.palette.collaborate[500],
  '--theme-color-green': theme.palette.userPreference.GREEN,
  '--theme-color-orange': theme.palette.userPreference.ORANGE,
  '--theme-color-pink': theme.palette.pink[700],
  '--theme-color-gray': theme.palette.grey[400],

  '--theme-highlight-default': 'transparent',
  '--theme-highlight-purple': alpha(theme.palette.communicate[500], 0.4),
  '--theme-highlight-red': alpha(theme.palette.error.main, 0.4),
  '--theme-highlight-yellow': alpha(theme.palette.warning.main, 0.4),
  '--theme-highlight-blue': alpha(theme.palette.collaborate[500], 0.4),
  '--theme-highlight-green': alpha(theme.palette.userPreference.GREEN, 0.4),
  '--theme-highlight-orange': alpha(theme.palette.userPreference.ORANGE, 0.4),
  '--theme-highlight-pink': alpha(theme.palette.pink[700], 0.4),
  '--theme-highlight-gray': alpha(theme.palette.grey[400], 0.4),

  '--theme-color-stone-100': theme.palette.grey[100],
  '--theme-color-stone-200': theme.palette.grey[200],
  '--theme-color-stone-20004': alpha(theme.palette.grey[200], 0.4),
  '--theme-color-stone-300': theme.palette.grey[300],
  '--theme-color-stone-30004': alpha(theme.palette.grey[300], 0.4),
  '--theme-color-stone-30008': alpha(theme.palette.grey[300], 0.8),
  '--theme-color-stone-400': theme.palette.grey[400],
  '--theme-color-stone-600': theme.palette.grey[600],
  '--theme-color-stone-700': theme.palette.grey[700],
  '--theme-color-stone-800': theme.palette.grey[800],
  '--theme-color-stone-900': theme.palette.grey[900],
}));

export const EditorStyles = styled(EditorStylesBase)(({ theme }) => ({
  '.m-0': {
    margin: 0,
  },
  '.my-4': {
    marginTop: 'calc(var(--theme-spacing) * 4)',
    marginBottom: 'calc(var(--theme-spacing) * 4)',
  },
  '.-mb-2': {
    marginBottom: 'calc(var(--theme-spacing) * -2)',
  },
  '.-mt-2': {
    marginTop: 'calc(var(--theme-spacing) * -2)',
  },
  '.mb-6': {
    marginBottom: 'calc(var(--theme-spacing) * 6)',
  },
  '.mt-4': {
    marginTop: 'calc(var(--theme-spacing) * 4)',
  },
  '.flex': {
    display: 'flex',
  },
  '.cursor-pointer': {
    cursor: 'pointer',
  },
  '.list-outside': {},
  '.list-decimal': {
    listStyleType: 'decimal',
  },
  '.list-disc': {
    listStyleType: 'disc',
  },
  '.items-start': {
    alignItems: 'flex-start',
  },
  '.rounded-lg': {
    borderRadius: 'calc(var(--theme-spacing) * 2)',
  },
  '.rounded-md': {
    borderRadius: 'calc(var(--theme-spacing) * 1.5)',
  },
  '.rounded-sm': {
    borderRadius: 'calc(var(--theme-spacing) * 0.5)',
  },
  '.border': {
    borderWidth: '1px',
  },
  '.border-solid': {
    borderStyle: 'solid',
  },
  '.border-l-4': {
    borderLeftWidth: 'calc(var(--theme-spacing) * 0.5)',
  },
  '.border-t': {
    borderTopWidth: '1px',
  },
  '.border-stone-200': {
    '--tw-border-opacity': 1,
    borderColor: 'var(--theme-color-stone-200)',
  },
  '.border-stone-300': {
    '--tw-border-opacity': 1,
    borderColor: 'var(--theme-color-stone-300)',
  },
  '.border-stone-700': {
    '--tw-border-opacity': 1,
    borderColor: 'var(--theme-color-stone-700)',
  },
  '.bg-stone-100': {
    '--tw-bg-opacity': 1,
    backgroundColor: 'var(--theme-color-stone-100)',
  },
  '.bg-stone-200': {
    '--tw-bg-opacity': 1,
    backgroundColor: 'var(--theme-color-stone-200)',
  },
  '.p-5': {
    padding: 'calc(var(--theme-spacing) * 1.25)',
  },
  '.px-1': {
    paddingLeft: 'calc(var(--theme-spacing) * 0.25)',
    paddingRight: 'calc(var(--theme-spacing) * 0.25)',
  },
  '.px-1\\.5': {
    paddingLeft: 'calc(var(--theme-spacing) * 0.375)',
    paddingRight: 'calc(var(--theme-spacing) * 0.375)',
  },
  '.py-1': {
    paddingTop: 'calc(var(--theme-spacing) * 0.25)',
    paddingBottom: 'calc(var(--theme-spacing) * 0.25)',
  },
  '.pl-2': {
    paddingLeft: 'calc(var(--theme-spacing) * 0.5)',
  },
  '.font-mono': {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
  },
  '.font-medium': {
    fontWeight: 500,
  },
  '.leading-3': {},
  '.leading-normal': {
    lineHeight: 1.5,
  },
  '.text-stone-400': {
    '--tw-text-opacity': 1,
    color: 'var(--theme-color-stone-400)',
  },
  '.text-stone-800': {
    '--tw-text-opacity': 1,
    color: 'var(--theme-color-stone-800)',
  },
  '.text-stone-900': {
    '--tw-text-opacity': 1,
    color: 'var(--theme-color-stone-900)',
  },
  '.underline': {
    textDecorationLine: 'underline',
  },
  '.underline-offset-\\[3px\\]': {
    textUnderlineOffset: '3px',
  },
  '.transition-colors': {
    transitionProperty:
      'color, background-color, border-color, fill, stroke, text-decoration-color',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDuration: '150ms',
  },
  '.hover\\:text-stone-600:hover': {
    '--tw-text-opacity': 1,
    color: 'var(--theme-color-stone-600)',
  },
  '.user-mention': {
    color: theme.palette.primary.main,
    padding: theme.spacing(0, 0.5),
    paddingBottom: theme.spacing(0.25),
    borderRadius: theme.spacing(0.5),
    backgroundColor: alpha(theme.palette.primary.main, 0.1),

    '&.--me': {
      color: theme.palette.warning.main,
      backgroundColor: alpha(theme.palette.warning.main, 0.2),
    },
  },
  blockquote: {
    margin: 0,
    padding: theme.spacing(1, 1, 1, 2),
    borderLeft: `4px solid ${theme.palette.grey[700]}`,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(0.5),
  },
  'p:not(:first-of-type)': { marginTop: theme.spacing(1) },
  h1: theme.typography.h2,
  h2: theme.typography.h3,
  h3: theme.typography.h4,
  h4: theme.typography.h4,
  h5: theme.typography.h4,
  h6: theme.typography.h4,
  'h1, h2, h3, h4, h5, h6': {
    margin: 0,
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(4),
  },
  '&:not(:hover)': {
    '.node-actions': {
      display: 'none',
    },
  },
  '.node-actions': {
    position: 'fixed',
    opacity: 1,
    transition: 'opacity ease-in 0.2s',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),

    '.action': {
      width: '1.2rem',
      height: '1.5rem',
      zIndex: 50,
      borderRadius: theme.spacing(0.5),

      '&:hover': {
        backgroundColor: alpha(
          theme.palette.action.hover,
          theme.palette.action.hoverOpacity,
        ),
        transition: 'background-color 0.2s',
      },

      '&:active': {
        backgroundColor: alpha(
          theme.palette.action.active,
          theme.palette.action.activatedOpacity,
        ),
        transition: 'background-color 0.2s',
        cursor: 'pointer',
      },
    },

    '.drag-handle': {
      backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10' style='fill: ${theme.palette.action.active}'><path d='M3,2 C2.44771525,2 2,1.55228475 2,1 C2,0.44771525 2.44771525,0 3,0 C3.55228475,0 4,0.44771525 4,1 C4,1.55228475 3.55228475,2 3,2 Z M3,6 C2.44771525,6 2,5.55228475 2,5 C2,4.44771525 2.44771525,4 3,4 C3.55228475,4 4,4.44771525 4,5 C4,5.55228475 3.55228475,6 3,6 Z M3,10 C2.44771525,10 2,9.55228475 2,9 C2,8.44771525 2.44771525,8 3,8 C3.55228475,8 4,8.44771525 4,9 C4,9.55228475 3.55228475,10 3,10 Z M7,2 C6.44771525,2 6,1.55228475 6,1 C6,0.44771525 6.44771525,0 7,0 C7.55228475,0 8,0.44771525 8,1 C8,1.55228475 7.55228475,2 7,2 Z M7,6 C6.44771525,6 6,5.55228475 6,5 C6,4.44771525 6.44771525,4 7,4 C7.55228475,4 8,4.44771525 8,5 C8,5.55228475 7.55228475,6 7,6 Z M7,10 C6.44771525,10 6,9.55228475 6,9 C6,8.44771525 6.44771525,8 7,8 C7.55228475,8 8,8.44771525 8,9 C8,9.55228475 7.55228475,10 7,10 Z'></path></svg>`,
      )}")`,
      backgroundSize: 'calc(0.5em + 0.375rem) calc(0.5em + 0.375rem)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      cursor: 'grab',

      '&:active': {
        cursor: 'grabbing',
      },
    },

    '.add-handle': {
      width: '1.2rem',
      height: '1.5rem',
      zIndex: 50,
      backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox="0 0 24 24" stroke-width="1.5" stroke="${theme.palette.action.active}" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>`,
      )}")`,
      backgroundSize: 'calc(0.5em + 0.375rem) calc(0.5em + 0.375rem)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      cursor: 'pointer',
    },

    '.ask-ai-handle': {
      width: '1.2rem',
      height: '1.5rem',
      zIndex: 50,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cmask id='path-1-inside-1_3045_840' fill='white'%3E%3Cpath d='M22.2353 9.54351C21.1189 5.07709 17.0272 1.76465 12.1505 1.76465C6.41456 1.76465 1.76465 6.34717 1.76465 12C1.76465 17.6528 6.41456 22.2353 12.1505 22.2353C17.0272 22.2353 21.1189 18.9229 22.2353 14.4565H18.4262C17.4346 16.915 14.9985 18.653 12.1505 18.653C10.918 18.653 9.76263 18.3275 8.7681 17.7589C7.71534 18.2066 6.59632 18.2085 5.58665 18.1617L6.69299 15.9167C5.87966 14.8179 5.3997 13.4642 5.3997 12C5.3997 8.32565 8.42214 5.34702 12.1505 5.34702C14.9985 5.34702 17.4346 7.08502 18.4262 9.54351H22.2353Z'/%3E%3Cpath d='M10.939 13.3317C10.0859 12.479 8.92887 12 7.72239 12C8.92887 12 10.0859 11.521 10.939 10.6683C11.7922 9.81556 12.2714 8.65906 12.2714 7.45317C12.2714 8.65906 12.7507 9.81556 13.6038 10.6683C14.4569 11.521 15.614 12 16.8205 12C15.614 12 14.4569 12.479 13.6038 13.3317C12.7507 14.1844 12.2714 15.3409 12.2714 16.5468C12.2714 15.3409 11.7922 14.1844 10.939 13.3317Z'/%3E%3C/mask%3E%3Cpath d='M22.2353 9.54351V12.9292H26.5715L25.52 8.72248L22.2353 9.54351ZM22.2353 14.4565L25.52 15.2775L26.5715 11.0708H22.2353V14.4565ZM18.4262 14.4565V11.0708H16.1412L15.2863 13.1899L18.4262 14.4565ZM8.7681 17.7589L10.4484 14.8196L8.98954 13.9856L7.44313 14.6432L8.7681 17.7589ZM5.58665 18.1617L2.5497 16.665L0.263595 21.3039L5.42961 21.5437L5.58665 18.1617ZM6.69299 15.9167L9.72994 17.4134L10.6425 15.5617L9.4143 13.9024L6.69299 15.9167ZM18.4262 9.54351L15.2863 10.81L16.1412 12.9292H18.4262V9.54351ZM7.72239 8.61428C5.85252 8.61428 4.33668 10.1301 4.33668 12C4.33668 13.8699 5.85252 15.3857 7.72239 15.3857V8.61428ZM8.88573 16.5468C8.88573 18.4167 10.4016 19.9325 12.2714 19.9325C14.1413 19.9325 15.6571 18.4167 15.6571 16.5468H8.88573ZM16.8205 15.3857C18.6903 15.3857 20.2062 13.8699 20.2062 12C20.2062 10.1301 18.6903 8.61428 16.8205 8.61428V15.3857ZM15.6571 7.45317C15.6571 5.58329 14.1413 4.06746 12.2714 4.06746C10.4016 4.06746 8.88573 5.58329 8.88573 7.45317H15.6571ZM12.1505 5.15036C15.468 5.15036 18.2097 7.39999 18.9507 10.3645L25.52 8.72248C24.0282 2.7542 18.5864 -1.62106 12.1505 -1.62106V5.15036ZM5.15036 12C5.15036 8.26347 8.23766 5.15036 12.1505 5.15036V-1.62106C4.59145 -1.62106 -1.62106 4.43086 -1.62106 12H5.15036ZM12.1505 18.8496C8.23766 18.8496 5.15036 15.7365 5.15036 12H-1.62106C-1.62106 19.5691 4.59145 25.621 12.1505 25.621V18.8496ZM18.9507 13.6354C18.2097 16.6 15.468 18.8496 12.1505 18.8496V25.621C18.5864 25.621 24.0282 21.2458 25.52 15.2775L18.9507 13.6354ZM22.2353 11.0708H18.4262V17.8422H22.2353V11.0708ZM15.2863 13.1899C14.8024 14.3896 13.5943 15.2673 12.1505 15.2673V22.0387C16.4027 22.0387 20.0667 19.4403 21.5661 15.723L15.2863 13.1899ZM12.1505 15.2673C11.5208 15.2673 10.943 15.1023 10.4484 14.8196L7.08779 20.6982C8.58224 21.5526 10.3152 22.0387 12.1505 22.0387V15.2673ZM7.44313 14.6432C7.14979 14.768 6.70529 14.8242 5.74368 14.7796L5.42961 21.5437C6.48734 21.5928 8.28089 21.6452 10.0931 20.8746L7.44313 14.6432ZM8.62359 19.6583L9.72994 17.4134L3.65605 14.4201L2.5497 16.665L8.62359 19.6583ZM9.4143 13.9024C9.01548 13.3636 8.78541 12.7109 8.78541 12H2.01399C2.01399 14.2175 2.74384 16.2723 3.97169 17.931L9.4143 13.9024ZM8.78541 12C8.78541 10.242 10.2452 8.73273 12.1505 8.73273V1.96131C6.59903 1.96131 2.01399 6.40935 2.01399 12H8.78541ZM12.1505 8.73273C13.5943 8.73273 14.8024 9.61034 15.2863 10.81L21.5661 8.27697C20.0667 4.5597 16.4027 1.96131 12.1505 1.96131V8.73273ZM18.4262 12.9292H22.2353V6.1578H18.4262V12.9292ZM7.72239 15.3857C8.03146 15.3857 8.32754 15.5084 8.54558 15.7264L13.3325 10.9371C11.8443 9.44962 9.82629 8.61428 7.72239 8.61428V15.3857ZM8.54558 15.7264C8.76356 15.9442 8.88573 16.2394 8.88573 16.5468H15.6571C15.6571 14.4424 14.8208 12.4246 13.3325 10.9371L8.54558 15.7264ZM15.6571 16.5468C15.6571 16.2394 15.7793 15.9442 15.9973 15.7264L11.2103 10.9371C9.72211 12.4246 8.88573 14.4424 8.88573 16.5468H15.6571ZM15.9973 15.7264C16.2153 15.5084 16.5114 15.3857 16.8205 15.3857V8.61428C14.7166 8.61428 12.6985 9.44962 11.2103 10.9371L15.9973 15.7264ZM16.8205 8.61428C16.5114 8.61428 16.2153 8.49155 15.9973 8.27361L11.2103 13.0629C12.6985 14.5504 14.7166 15.3857 16.8205 15.3857V8.61428ZM15.9973 8.27361C15.7793 8.05574 15.6571 7.76058 15.6571 7.45317H8.88573C8.88573 9.55754 9.72211 11.5754 11.2103 13.0629L15.9973 8.27361ZM8.88573 7.45317C8.88573 7.76058 8.76356 8.05574 8.54558 8.27361L13.3325 13.0629C14.8208 11.5754 15.6571 9.55754 15.6571 7.45317H8.88573ZM8.54558 8.27361C8.32754 8.49155 8.03146 8.61428 7.72239 8.61428V15.3857C9.82629 15.3857 11.8443 14.5504 13.3325 13.0629L8.54558 8.27361Z' fill='%23637381' mask='url(%23path-1-inside-1_3045_840)'/%3E%3C/svg%3E%0A")`,
      backgroundSize: 'calc(0.5em + 0.375rem) calc(0.5em + 0.375rem)',

      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      cursor: 'pointer',
    },

    '&.hide': {
      opacity: 0,
      pointerEvents: 'none',
    },
    '@media screen and (max-width: 600px)': {
      display: 'none',
      pointerEvents: 'none',
    },
  },
  '.collaboration-cursor': {
    ...Object.entries(theme.palette.userPreference).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [`&.--${key}`]: {
          '--collaboration-cursor-color': value,
          '--collaboration-cursor-color-a': alpha(value as string, 0.47),
          '--collaboration-cursor-text': theme.palette.getContrastText(
            value as string,
          ),

          '&:not(.collaboration-cursor__selection):hover': {
            '--collaboration-cursor-color': alpha(value as string, 0.2),
            '--collaboration-cursor-text': theme.palette.text.secondary,
          },
        },
      }),
      {} as Record<string, any>,
    ),

    '&__caret': {
      marginLeft: '-1px',
      marginRight: '-1px',
      // pointerEvents: 'none',
      position: 'relative',
      wordBreak: 'normal',
      border: '1px solid var(--collaboration-cursor-color)',
    },
    '&__label': {
      border: '1px solid var(--collaboration-cursor-color)',
      backgroundColor: 'var(--collaboration-cursor-color)',
      borderRadius: '3px 3px 3px 0',
      color: 'var(--collaboration-cursor-text)',
      fontSize: theme.typography.body2.fontSize,
      fontStyle: 'normal',
      fontWeight: 600,
      left: '-1px',
      lineHeight: 'normal',
      padding: '0.1rem 0.3rem',
      position: 'absolute',
      top: '-1.4em',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      userSelect: 'none',
      whiteSpace: 'nowrap',
    },
    '&__selection': {
      backgroundColor: 'var(--collaboration-cursor-color-a)',
      color: 'var(--collaboration-cursor-text)',
    },
  },
  hr: {
    marginTop: 'calc(var(--theme-spacing) * 4)',
    marginBottom: 'calc(var(--theme-spacing) * 6)',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderColor: 'var(--theme-color-stone-300)',
  },
  'code:not([data-node-view-wrapper])': {
    fontFamily: 'Cousine',
    lineHeight: 1.75,
    fontSize: '0.875rem',
    padding: '0.125rem 0.25rem',
    borderRadius: '0.25rem',
    backgroundColor: alpha(theme.palette.warning.darker, 0.1),
    color: theme.palette.warning.dark,
  },
  'a[href]': {
    color: 'var(--theme-color-stone-600)',
    textDecoration: 'underline',
    underlineOffset: '3px',
    cursor: 'pointer',
    transition: theme.transitions.create(['color', 'text-decoration-color'], {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.sharp,
    }),
    '&:hover': {
      color: 'var(--theme-color-stone-500)',
    },
  },

  '.tiptap > ul, .tiptap > ol, .tiptap > ul ul': {
    lineHeight: 'calc(var(--theme-spacing) * 0.75)',
    listStylePosition: 'outside',
    paddingLeft: 'calc(var(--theme-spacing) * 4)',
    marginTop: 'calc(var(--theme-spacing) * 2)',
    li: {
      lineHeight: 1.25,
    },
  },
  '.tiptap > ol': {
    listStyleType: 'decimal',
  },
  '.tiptap > ul': {
    listStyleType: 'disc',
  },
  'ul[data-type="taskList"]': {
    listStyleType: 'none',
    paddingLeft: '0',
    padding: theme.spacing(2, 0),
    li: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),

      label: {
        display: 'flex',
      },
      p: {
        margin: 0,
        marginTop: theme.spacing(0.25),
      },
      '&[data-checked="true"]': {
        textDecoration: 'line-through',
        color: theme.palette.text.secondary,
      },
      '&:not(:last-of-type)': {
        marginBottom: theme.spacing(1),
      },
    },
    'input[type="checkbox"]': {
      appearance: 'none',
      position: 'relative',
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      // paddingRight: theme.spacing(1.5),
      borderRadius: theme.spacing(0.5),
      border: `1px solid ${theme.palette.action.active}`,
      cursor: 'pointer',
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create([
        'background-color',
        'border-color',
      ]),
      '&:hover': {
        borderColor: theme.palette.action.active,
        backgroundColor: theme.palette.action.hover,
      },
      '&:checked': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23fff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M5 13l4 4L19 7' /%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'contain',
        },
      },
    },
  },
  '.virtual-selection': {
    backgroundColor: 'var(--theme-color-stone-30004)',
  },
  '.virtual-selection-draggable': {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
  },
  table: {
    borderCollapse: 'collapse',
    margin: 0,
    overflow: 'hidden',
    tableLayout: 'fixed',
    width: '100%',

    'td, th': {
      border: `1px solid ${theme.palette.action.active}`,
      boxSizing: 'border-box',
      minWidth: '1em',
      padding: theme.spacing(1),
      position: 'relative',
      verticalAlign: 'top',
    },

    th: {
      backgroundColor: theme.palette.background.paper,
      fontWeight: 600,
      textAlign: 'left',
    },

    //? uncomment when add support to cell selection actions
    // '.selectedCell:after': {
    //   background: alpha(theme.palette.action.active, 0.24),
    //   content: '""',
    //   left: 0,
    //   right: 0,
    //   top: 0,
    //   bottom: 0,
    //   pointerEvents: 'none',
    //   position: 'absolute',
    //   zIndex: 2,
    // },

    '.column-resize-handle': {
      backgroundColor: theme.palette.primary.main,
      bottom: '-2px',
      pointerEvents: 'none',
      position: 'absolute',
      right: '-2px',
      top: '0',
      width: '4px',
    },
  },

  '.tableWrapper': {
    margin: theme.spacing(3, 0),
    overflowX: 'auto',
  },

  '&.resize-cursor': {
    cursor: ['col-resize', 'ew-resize'],
  },

  '.column-resize-handle': {
    backgroundColor: theme.palette.primary.main,
    // bottom: '-2px',
    pointerEvents: 'none',
    position: 'absolute',
    right: '-2px',
    top: '0',
    width: '4px',
  },

  //! table drag and drop styles
  // '.draggable-column-header': {
  //   cursor: 'grab',
  //   backgroundColor: alpha(theme.palette.action.active, 0.02),
  // },

  // '.drag-overlay': {
  //   position: 'absolute',
  //   pointerEvents: 'none',
  //   zIndex: 99,
  //   border: `2px solid ${theme.palette.primary.main}`,
  //   boxShadow: `0 0 10px 4px ${alpha(theme.palette.primary.main, 0.6)}`,
  //   borderRadius: 2,
  //   animation: 'glow-fade 1s ease-in-out infinite',
  // },

  // '.drop-target-overlay': {
  //   position: 'absolute',
  //   pointerEvents: 'none',
  //   zIndex: 999,
  //   border: `2px solid ${theme.palette.primary.main}`,
  //   boxShadow: `0 0 10px 4px ${alpha(theme.palette.primary.main, 0.6)}`,
  //   borderRadius: 2,
  //   animation: 'glow-fade 1s ease-in-out infinite',
  // },
}));
